import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useSpring, animated } from "react-spring";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import NorthIcon from "@mui/icons-material/North";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import TopArrow from "../../../../assets/Group.png";
import lightbulb from "../../../../assets/lightbulb.png";
import yelloBulb from "../../../../assets/lightbulb1.png";
import apiEndPointsConfig from "../../../../services/apiEndPoints";
import axiosInstance from "../../../../services/axiosInstance";

const validationSchema = Yup.object({
  question1: Yup.mixed().required("field is required"), // Making companyName required
  question2: Yup.mixed().required("field is required"), // Making industryType required
  question3: Yup.mixed().required("field is required"), // Making name required
  question4: Yup.mixed().required("field is required"), // Making name required
  question5: Yup.mixed().required("field is required"), // Making name required
});

const steps = [
  {
    title: "Verify Your Account",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Enter countryName",
  },
  {
    title: "Setting up your call center",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Enter company name",
  },
  {
    title: "Base Setup",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Select industry type",
  },
  {
    title: "Select your company size",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Select company size",
  },
  {
    title: "Define Working hours",
    description:
      "Share your business details to ensure our agents provide accurate and professional responses to your customers.",
    placeholder: "Define working hours",
  },
];

const TravelMobile = ({ nextStep }) => {
  const navigate = useNavigate();
  const sections = useRef([]);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [activeStep, setActiveStep] = React.useState(0);

  const scrollToSection = (index) => {
    sections.current[index]?.scrollIntoView({ behavior: "smooth" });
  };

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const addFaqDetails = async (data) => {
    const access_token = token;

    try {
      const response = await axiosInstance.post(
        `${apiEndPointsConfig.saveFaqDetails}`,
        data
      );
      navigate("/number");
      console.log("Response:", response.data);
    } catch (error) {
      console.log(error);
    }
  };
  const formik = useFormik({
    initialValues: {
      question1: "",
      question2: "",
      question3: "",
      question4: "",
      question5: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = {
        userId: userId,
        faqs: [
          {
            faq_title: "How do I make a hotel reservation?",
            faq_answer: values.question1,
          },
          {
            faq_title: "What is your cancellation policy for bookings?",
            faq_answer: values.question2,
          },
          {
            faq_title: "Do you offer airport shuttle services?",
            faq_answer: values.question3,
          },
          {
            faq_title: "Can I modify my booking after confirmation?",
            faq_answer: values.question4,
          },
          {
            faq_title: "Do you have loyalty or reward programs?",
            faq_answer: values.question5,
          },
        ],
      };

      addFaqDetails(data);
    },
  });

  const handleNext = async () => {
    const stepFields = {
      0: ["question1"],
      1: ["question2"],
      2: ["question3"],
      3: ["question4"],
      4: ["question5"],
    };

    const currentFields = stepFields[currentStep];

    formik.setTouched(
      currentFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    const errors = await formik.validateForm();

    const hasErrors = currentFields.some((field) => errors[field]);

    if (!hasErrors) {
      if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => {
          const nextStep = prev + 1;
          scrollToSection(nextStep);
          return nextStep;
        });
        setActiveStep((prev) => prev + 1);
      } else {
        formik.handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => {
        const prevStep = prev - 1;
        scrollToSection(prevStep);
        setActiveStep((prev) => prev - 1);
        return prevStep;
      });
    }
  };

  const fade = useSpring({ opacity: 1, from: { opacity: 0 } });

  const handleCheckboxChange = (event, question) => {
    const { checked } = event.target;
    const defaultResponses = {
      question1:
        "Reservations can be made on our website or by calling our booking center.",
      question2:
        "Cancellations made 48 hours before check-in are fully refundable.",
      question3:
        "Yes, we provide complimentary airport transfers for premium bookings.",
      question4:
        "	Yes, you can modify your booking from your profile or by contacting customer care.",
      question5:
        "Yes, join our loyalty program to earn points on every booking.",
    };
    formik.setFieldValue(question, checked ? defaultResponses[question] : "");
  };
  // Initial state for each lightbulb
  const [bulbStates, setBulbStates] = useState({
    1: currentStep === 0 ? lightbulb : yelloBulb,
    2: currentStep === 1 ? yelloBulb : lightbulb,
    3: currentStep === 2 ? yelloBulb : lightbulb,
    4: currentStep === 3 ? yelloBulb : lightbulb,
    5: currentStep === 4 ? yelloBulb : lightbulb,
  });

  // Predefined answers for each bulb
  const bulbAnswers = {
    1: "You can easily make a hotel reservation through our website or mobile app by selecting your destination, travel dates, and preferred hotel. Just follow the booking steps and confirm your payment to secure your stay.",
    2: "Our cancellation policy varies by hotel and package. Many bookings offer free cancellation within a specific timeframe. Full details are provided during the booking process and in your confirmation email.",
    3: "Yes, we offer airport shuttle services with select travel packages and hotel bookings. You can add this option during checkout or contact our support team for assistance.",
    4: "Yes, you can modify your booking based on the hotel or service provider’s policy. Simply log in to your account or contact our support team for help with changes.",
    5: "Yes! We offer a loyalty program where you earn points for every booking—hotels, flights, tours, and more. You can redeem these points for discounts on future travel, exclusive upgrades, or special perks. The more you travel with us, the more rewards you unlock!",
  };

  // Handle bulb click
  const handleBulbClick = (bulbId) => {
    setBulbStates((prevState) => {
      const newState = { ...prevState };
      const isYellow = prevState[bulbId] === yelloBulb;

      // Toggle bulb state
      newState[bulbId] = isYellow ? lightbulb : yelloBulb;

      // Update formik field value based on state
      const questionKey = `question${bulbId}`;
      formik.setFieldValue(questionKey, isYellow ? "" : bulbAnswers[bulbId]);

      return newState;
    });
  };
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Grid container>
        <Grid item md={9.5} xs={12}>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid
                container
                sx={{
                  mt: {
                    xs: "10px",
                    md: "100px",
                  },
                  p: "0 20px",
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={9.5}
                  sx={{ display: { xs: "initial", md: "none" } }}
                >
                  <Box>
                    <Box
                      sx={{
                        // height: { xs: "27vh", md: "36vh" },
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: "100%", md: "50%" },
                        }}
                      >
                        <animated.div style={fade}>
                          {/* Step 1 */}
                          {currentStep === 0 && (
                            <div ref={(el) => (sections.current[0] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      currentStep !== 0 ? "lightgrey" : "black",
                                    opacity: currentStep !== 0 ? 0.5 : 1,
                                  }}
                                >
                                  How do I make a hotel reservation?
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ mr: "0px" }}>
                                    <img
                                      className="lightbulb-image"
                                      src={bulbStates[1]}
                                      style={{
                                        width: "30px",
                                        cursor: "pointer",
                                      }}
                                      alt="Suggestion"
                                      onClick={() => handleBulbClick(1)}
                                    />
                                  </Box>
                                  <Typography>Hint</Typography>
                                </Box>
                              </Box>
                              <TextField
                                name="question1"
                                id="question1"
                                variant="filled"
                                margin="normal"
                                fullWidth
                                rows={3}
                                multiline
                                value={formik.values.question1}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={currentStep !== 0}
                                error={
                                  formik.touched.question1 &&
                                  Boolean(formik.errors.question1)
                                }
                                helperText={
                                  formik.touched.question1 &&
                                  formik.errors.question1
                                    ? formik.errors.question1
                                    : ""
                                }
                                sx={{ opacity: currentStep !== 0 ? 0.3 : 1 }}
                              />
                            </div>
                          )}

                          {/* Step 2 */}
                          {currentStep === 1 && (
                            <div ref={(el) => (sections.current[1] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      currentStep !== 1 ? "lightgrey" : "black",
                                    opacity: currentStep !== 1 ? 0.5 : 1,
                                  }}
                                >
                                  What is your cancellation policy for bookings?
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ mr: "0px" }}>
                                    <img
                                      className="lightbulb-image"
                                      src={bulbStates[2]}
                                      style={{
                                        width: "30px",
                                        cursor: "pointer",
                                      }}
                                      alt="Suggestion"
                                      onClick={() => handleBulbClick(2)}
                                    />
                                  </Box>
                                  <Typography>Hint</Typography>
                                </Box>
                              </Box>

                              <TextField
                                sx={{ opacity: currentStep !== 1 ? 0.3 : 1 }}
                                name="question2"
                                id="question2"
                                value={formik.values.question2}
                                onChange={formik.handleChange}
                                fullWidth
                                rows={3}
                                multiline
                                variant="filled"
                                margin="normal"
                                disabled={currentStep !== 1}
                                error={
                                  formik.touched.question2 &&
                                  Boolean(formik.errors.question2)
                                }
                                helperText={
                                  formik.touched.question2 &&
                                  formik.errors.question2
                                    ? formik.errors.question2
                                    : ""
                                }
                              />
                            </div>
                          )}

                          {/* Step 3 */}
                          {currentStep === 2 && (
                            <div ref={(el) => (sections.current[2] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      currentStep !== 2 ? "lightgrey" : "black",
                                    opacity: currentStep !== 2 ? 0.5 : 1,
                                  }}
                                >
                                  Do you offer airport shuttle services?
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ mr: "0px" }}>
                                    <img
                                      className="lightbulb-image"
                                      src={bulbStates[3]}
                                      style={{
                                        width: "30px",
                                        cursor: "pointer",
                                      }}
                                      alt="Suggestion"
                                      onClick={() => handleBulbClick(3)}
                                    />
                                  </Box>
                                  <Typography>Hint</Typography>
                                </Box>
                              </Box>

                              <TextField
                                sx={{ opacity: currentStep !== 2 ? 0.3 : 1 }}
                                id="question3"
                                name="question3"
                                value={formik.values.question3}
                                onChange={formik.handleChange}
                                fullWidth
                                rows={3}
                                multiline
                                variant="filled"
                                margin="normal"
                                disabled={currentStep !== 2}
                                error={
                                  formik.touched.question3 &&
                                  Boolean(formik.errors.question3)
                                }
                                helperText={
                                  formik.touched.question3 &&
                                  formik.errors.question3
                                    ? formik.errors.question3
                                    : ""
                                }
                              />
                            </div>
                          )}

                          {/* Step 4 */}
                          {currentStep === 3 && (
                            <div ref={(el) => (sections.current[3] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      currentStep !== 3 ? "lightgrey" : "black",
                                  }}
                                >
                                  Can I modify my booking after confirmation?
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ mr: "0px" }}>
                                    <img
                                      className="lightbulb-image"
                                      src={bulbStates[4]}
                                      style={{
                                        width: "30px",
                                        cursor: "pointer",
                                      }}
                                      alt="Suggestion"
                                      onClick={() => handleBulbClick(4)}
                                    />
                                  </Box>
                                  <Typography>Hint</Typography>
                                </Box>
                              </Box>

                              <TextField
                                id="question4"
                                name="question4"
                                value={formik.values.question4}
                                onChange={formik.handleChange}
                                fullWidth
                                rows={3}
                                multiline
                                variant="filled"
                                margin="normal"
                                disabled={currentStep !== 3}
                                error={
                                  formik.touched.question4 &&
                                  Boolean(formik.errors.question4)
                                }
                                helperText={
                                  formik.touched.question4 &&
                                  formik.errors.question4
                                    ? formik.errors.question4
                                    : ""
                                }
                              />
                            </div>
                          )}

                          {/* Step 5 */}
                          {currentStep === 4 && (
                            <div ref={(el) => (sections.current[4] = el)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      currentStep !== 4 ? "lightgrey" : "black",
                                  }}
                                >
                                  Do you have loyalty or reward programs?
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ mr: "0px" }}>
                                    <img
                                      className="lightbulb-image"
                                      src={bulbStates[5]}
                                      style={{
                                        width: "30px",
                                        cursor: "pointer",
                                      }}
                                      alt="Suggestion"
                                      onClick={() => handleBulbClick(5)}
                                    />
                                  </Box>
                                  <Typography>Hint</Typography>
                                </Box>
                              </Box>

                              <TextField
                                id="question5"
                                name="question5"
                                value={formik.values.question5}
                                onChange={formik.handleChange}
                                fullWidth
                                rows={3}
                                multiline
                                variant="filled"
                                margin="normal"
                                disabled={currentStep !== 4}
                                error={
                                  formik.touched.question5 &&
                                  Boolean(formik.errors.question5)
                                }
                                helperText={
                                  formik.touched.question5 &&
                                  formik.errors.question5
                                    ? formik.errors.question5
                                    : ""
                                }
                              />
                            </div>
                          )}
                        </animated.div>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{
                    ml: {
                      xs: "0px",
                      md: "30px",
                    },
                    p: {
                      xs: "20px 0 50px 0",
                      md: "0 0 0px 0",
                    },
                    display: {
                      xs: "flex",
                      md: "initial",
                    },
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    color="primary"
                    onClick={handleBack}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {/* <img src={TopArrow} /> */}
                    {currentStep == 0 ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: "2.5rem",
                          color: "lightgrey",
                          fontWeight: 100,
                        }}
                      />
                    ) : (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: "2.5rem",
                          color: "#2C75D3",
                          fontWeight: 100,
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNext();
                    }}
                    sx={{
                      whiteSpace: "nowrap",
                      textTransform: "capitalize",
                      mt: {
                        xs: "0px",
                        md: "15px",
                      },
                      ml: {
                        xs: "30px",
                        md: "0",
                      },
                    }}
                  >
                    Save & go next
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TravelMobile;
